import * as React from 'react';
import styled from 'styled-components';
import dynamic from 'next/dynamic';
import Headline from '@adecco/base-app/src/components/atoms/Headline/Headline';
import Benefit, { IBenefit } from '@adecco/base-app/src/components/atoms/Benefit/Benefit';
import { moreThan } from '@adecco/base-app/src/constants/breakpoints/breakpoints';
import Button from '@adecco/base-app/src/components/atoms/Button/Button';
import calcrem from '@adecco/base-app/src/utilities/calcrem/calcrem';
import { grid_1_2_3 } from '@adecco/base-app/src/styles/grids';
import Container from '@adecco/base-app/src/components/atoms/Container/Container';
import { useisMobile } from '@adecco/base-app/src/hooks/useIsMobile';

const Carousel = dynamic(() => import('@adecco/base-app/src/components/atoms/Carousel/Carousel'));

export interface IBenefitList {
  title?: string;
  entries: Array<IBenefit>;
  ctaTitle?: string;
  ctaLink?: string;
  centeredHeadline?: boolean;
}

const BenefitListItems = styled.div`
  ${grid_1_2_3};
  gap: ${calcrem(20)} ${calcrem(20)};
  display: none;

  ${moreThan('md')} {
    display: grid;
    margin-bottom: ${(props) => props.theme.margins.benefitListItems};
  }
`;

const BenefitListItem = styled.div`
  width: 100%;
  height: 100%;
  padding: ${calcrem(5)} ${calcrem(15)} ${calcrem(15)};

  > * {
    height: 100%;
  }

  ${moreThan('md')} {
    padding: 0;
    height: auto;
  }
`;

const BenefitHeadline = styled(Headline)`
  margin-bottom: ${(props) => props.theme.margins.benefitHeadline};
  font-size: ${(props) => props.theme.fontSizes.headline2};
  line-height: ${(props) => props.theme.lineHeights.headline2};
  font-family: ${(props) => props.theme.fonts.HeadlineBold};

  ${moreThan('md')} {
    font-size: ${(props) => props.theme.fontSizes.headline2WideScreen};
    margin-bottom: ${(props) => props.theme.margins.benefitHeadlineWideScreen};
    line-height: ${(props) => props.theme.lineHeights.headline2WideScreen};
  }
`;

const BenefitCarousel = styled.div`
  display: block;
  margin: ${(props) => props.theme.margins.benefitCarousel};

  ${moreThan('md')} {
    display: none;
  }
`;

const ButtonStyled = styled(Button)`
  width: fit-content;
`;

const BenefitList: React.FunctionComponent<IBenefitList> = ({
  title,
  entries,
  ctaTitle,
  ctaLink,
  centeredHeadline,
}) => {
  const { isMobileScreen } = useisMobile();

  const benefitItems = entries.map((entry, index) => (
    <BenefitListItem
      key={`jobFact-slider-${entry.name}-${index}`}
      data-value={index + 1}
      className="item"
    >
      <Benefit {...entry} />
    </BenefitListItem>
  ));

  return (
    <Container>
      <BenefitHeadline skin={2} centered={centeredHeadline}>
        {title}
      </BenefitHeadline>
      {isMobileScreen ? (
        <BenefitCarousel>
          <Carousel items={benefitItems} />
        </BenefitCarousel>
      ) : (
        <BenefitListItems>
          {entries.map((entry) => (
            <BenefitListItem key={`jobFact-${entry.name}`}>
              <Benefit {...entry} />
            </BenefitListItem>
          ))}
        </BenefitListItems>
      )}
      {ctaLink && ctaTitle && (
        <ButtonStyled href={ctaLink} variant="small-ghost">
          {ctaTitle}
        </ButtonStyled>
      )}
    </Container>
  );
};

export default BenefitList;
